import { useLayoutEffect, useRef } from 'react';
import { NavigationType, useLocation, useNavigationType } from 'react-router-dom';

/**
 * Opens every newly visited page at the top.
 *
 * A client-side navigation swaps the page but leaves the window where it was,
 * so a link clicked at the bottom of one page opened the next one at ITS
 * bottom (footer "Privacy Policy" landed on the last clause of the policy).
 * Rendered once inside <BrowserRouter>, so it covers every route — the public
 * site and the signed-in app alike.
 *
 * It acts only when the pathname changes on a PUSH or REPLACE:
 *   - POP is Back/Forward (and the first load). The browser restores the
 *     position the visitor had there; that is left alone.
 *   - A query-string or hash change alone is still the same page (admin
 *     filters, /plans?highlight=…) and must not move it.
 *
 * useLayoutEffect rather than useEffect: the reset lands before the new page
 * is painted (no flash at the old offset) and before any page effect that
 * scrolls to a specific element, so that element still wins.
 */
export default function ScrollToTop() {
  const { pathname } = useLocation();
  const navigationType = useNavigationType();
  const lastPathname = useRef(pathname);

  useLayoutEffect(() => {
    if (lastPathname.current === pathname) return;
    lastPathname.current = pathname;
    if (navigationType !== NavigationType.Pop) window.scrollTo(0, 0);
  }, [pathname, navigationType]);

  return null;
}
